import { useMemo } from 'react';

import { FEATURE_FLAGS_FOR_SELLER } from '@savgroup-front-common/constants';
import {
  CREATE_MODAL_TYPES,
  PERMISSIONS,
} from '@savgroup-front-common/constants/control';
import { useGetFeatureFlagsQuery } from '@savgroup-front-common/core/api/Authorization/useGetFeatureFlagsQuery';
import { useHasPermission } from '@savgroup-front-common/core/hooks/useHasPermission';
import { useIsSuperAdmin } from '@savgroup-front-common/core/hooks/useHasRole';

import { useAppContext } from '@/view/app/App.context';

const useAuthorizedCreateModalTypes = (): CREATE_MODAL_TYPES[] => {
  const { selectedSeller } = useAppContext();

  const hasCreateFileByOrderReferencePermission = useHasPermission(
    PERMISSIONS.CREATE_FILE_BY_ORDER_REFERENCE,
  );
  const hasCreateFileByProductReferencePermission = useHasPermission(
    PERMISSIONS.CREATE_FILE_BY_PRODUCT_REFERENCE,
  );
  const hasCreateFileByReceiptInformationPermission = useHasPermission(
    PERMISSIONS.CREATE_FILE_BY_RECEIPT_INFORMATION,
  );
  const hasCreateFileByCustomerInformationPermission = useHasPermission(
    PERMISSIONS.CREATE_FILE_BY_CUSTOMER_INFORMATION,
  );
  const hasCreateSparePartRequestPermission = useHasPermission(
    PERMISSIONS.CREATE_SPARE_PART_REQUEST,
  );
  const hasCreateFileOwnerStockPermission = useHasPermission(
    PERMISSIONS.CREATE_FILE_BY_OWNER_STOCK,
  );
  const hasNewCreateFileFeatureFlagEnabled = useGetFeatureFlagsQuery({
    featureFlag: [FEATURE_FLAGS_FOR_SELLER.NEW_CREATE_FILE],
    sellerId: selectedSeller?.id,
  });

  const isSuperAdmin = useIsSuperAdmin();

  return useMemo(() => {
    const authorizations: CREATE_MODAL_TYPES[] = [];

    const hasAnyCreateFilePermission =
      hasCreateFileByOrderReferencePermission ||
      hasCreateFileByProductReferencePermission ||
      hasCreateFileByCustomerInformationPermission ||
      hasCreateFileOwnerStockPermission ||
      hasCreateFileByReceiptInformationPermission;

    if (
      (hasNewCreateFileFeatureFlagEnabled && hasAnyCreateFilePermission) ||
      isSuperAdmin
    ) {
      authorizations.push(CREATE_MODAL_TYPES.NEW_CREATE_FILE);
    }

    if (
      !hasNewCreateFileFeatureFlagEnabled &&
      hasCreateFileByOrderReferencePermission
    ) {
      authorizations.push(CREATE_MODAL_TYPES.FILE_ORDER_REFERENCE);
    }

    if (
      !hasNewCreateFileFeatureFlagEnabled &&
      hasCreateFileByProductReferencePermission
    ) {
      authorizations.push(CREATE_MODAL_TYPES.FILE_PRODUCT_REFERENCE);
    }

    if (
      !hasNewCreateFileFeatureFlagEnabled &&
      hasCreateFileByCustomerInformationPermission
    ) {
      authorizations.push(CREATE_MODAL_TYPES.FILE_BY_CUSTOMER_INFORMATION);
    }

    if (
      !hasNewCreateFileFeatureFlagEnabled &&
      hasCreateFileOwnerStockPermission
    ) {
      authorizations.push(CREATE_MODAL_TYPES.FILE_OWNER_STOCK);
    }

    if (
      !hasNewCreateFileFeatureFlagEnabled &&
      hasCreateFileByReceiptInformationPermission
    ) {
      authorizations.push(CREATE_MODAL_TYPES.FILE_RECEIPT_INFORMATION);
    }

    if (isSuperAdmin) {
      authorizations.push(CREATE_MODAL_TYPES.SELLER);
      authorizations.push(CREATE_MODAL_TYPES.TENANT);
    }

    if (hasCreateSparePartRequestPermission) {
      authorizations.push(CREATE_MODAL_TYPES.SPARE_PART_REQUEST);
    }

    return authorizations;
  }, [
    hasCreateFileByOrderReferencePermission,
    hasCreateFileByProductReferencePermission,
    hasCreateFileByCustomerInformationPermission,
    hasCreateFileOwnerStockPermission,
    hasCreateFileByReceiptInformationPermission,
    isSuperAdmin,
    hasCreateSparePartRequestPermission,
    hasNewCreateFileFeatureFlagEnabled,
  ]);
};

export default useAuthorizedCreateModalTypes;
